import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Button } from "@/components/button";
import { ArrowLeftIcon, CheckCircleIcon } from "@heroicons/react/20/solid";
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  Bars3BottomLeftIcon,
  Bars3Icon,
  Bars3BottomRightIcon,
} from "@heroicons/react/24/outline";
import { useQueryGetExamPaper } from "@/apis/exam_papers/getExamPaper";
import { useQueryGetExamConfig } from "@/apis/exam_configs/getExamConfig";
import { Tabs, Spin, message, Switch, Input } from "antd";
import { Select } from "@/components/select";
import type { IQuestion } from "@/apis/exam_papers/types";
import { Text } from "@/components/text";
import { useUpdateQuestion } from "@/apis/questions/updateQuestion";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import katex from "katex";
import "katex/dist/katex.min.css";
import { apiUploadImage } from "@/apis/upload/apiUploadImage";
import CategorySelector from "@/pages/exam_papers/components/CategorySelector";
import QuestionNumberSelector from "@/pages/exam_papers/components/QuestionNumberSelector";
import { convertToFraction } from "@/utils/math";
import { getPaperTypeLabel } from "@/constants/exam";

import {
  useQueryGetPassage,
  useUpdatePassage,
  useCreatePassage,
} from "@/apis/passages";

// Quill과 KaTeX 설정
declare global {
  interface Window {
    katex: any;
    Quill: any;
  }
}

// KaTeX를 window에 연결하여 Quill에서 접근 가능하게 함
if (typeof window !== "undefined") {
  window.katex = katex;

  // Quill 모듈 등록
  if (typeof window.Quill !== "undefined") {
    try {
      const Formula = window.Quill.import("formats/formula");
      if (!Formula) {
        const Embed = window.Quill.import("blots/embed");

        class FormulaBlot extends Embed {
          static blotName = "formula";
          static tagName = "span";
          static className = "ql-formula";

          static create(value: string) {
            const node = super.create(value);
            if (typeof value === "string") {
              try {
                window.katex.render(value, node, {
                  throwOnError: false,
                  displayMode: false,
                });
                node.setAttribute("data-value", value);
              } catch (e) {
                node.textContent = value;
              }
            }
            return node;
          }

          static value(node: Element) {
            return node.getAttribute("data-value");
          }
        }

        window.Quill.register(FormulaBlot, true);
      }
    } catch (e) {
      console.warn("Quill formula module registration failed:", e);
    }
  }
}

// const { TabPane } = Tabs; // deprecated, using items instead

// Quill 모듈 설정
const noToolbarModules = {
  toolbar: false,
  keyboard: {
    bindings: {
      "formula-complete": {
        key: "Enter",
        handler: function (this: any, range: any, context: any) {
          const quill = this.quill;

          // 1) 커서 이전까지의 전체 텍스트를 가져옵니다.
          const text = quill.getText(0, range.index);

          // 2) 정규식으로 마지막 `$$...$$`를 찾습니다.
          //    여기서 `([\s\S]+?)`는 줄바꿈 포함 모든 문자에 매칭됩니다.
          //    /(\$\$)([\s\S]+?)(\$\$)$/ 형태를 사용해도 무방합니다.
          const match = text.match(/(\$\$)([\s\S]+)(\$\$)$/);

          // 3) 매칭된 수식이 없다면(=사용자가 입력한 $$...$$ 패턴이 없다면) 엔터 기본 동작을 수행합니다.
          if (!match) {
            return true; // 기본 Enter 동작 허용
          }

          // 4) 수식 내용(중간 그룹: match[2])만 추출
          const formulaText = match[2].trim();

          // 5) 문서에서 실제로 삭제해야 할 텍스트 길이(매칭된 전체 $$...$$ 길이) 계산
          const matchedTextLength = match[0].length;

          // 6) 실제 시작 인덱스 (현재 커서 위치 - 매칭된 전체 길이)
          const startIndex = range.index - matchedTextLength;

          // 7) 매칭된 $$...$$ 텍스트를 지우고, formula 블롯 삽입
          quill.deleteText(startIndex, matchedTextLength);
          quill.insertEmbed(startIndex, "formula", formulaText);

          // 8) 삽입 직후, 커서를 수식 뒤로 이동
          quill.setSelection(startIndex + 1);

          // 9) 기본 Enter 동작 막기
          return false;
        },
      },
    },
  },
  clipboard: {
    matchVisual: false,
    matchers: [
      [
        "IMG",
        (node: any, delta: any) => {
          return delta;
        },
      ],
      // Remove background colors and text colors
      [
        "*",
        (node: any, delta: any) => {
          // First, remove inline styles from the DOM node
          if (node.style) {
            node.removeAttribute('style');
          }
          
          // Then clean the delta ops
          const ops = delta.ops || [];
          return {
            ops: ops.map((op: any) => {
              if (op.attributes) {
                const { background, color, ...restAttributes } = op.attributes;
                return { 
                  ...op, 
                  attributes: Object.keys(restAttributes).length > 0 ? restAttributes : undefined 
                };
              }
              return op;
            })
          };
        },
      ],
    ],
  },
};

// 에디터 스타일 설정 (컴포넌트 외부에서 정의하여 재생성 방지)
const EDITOR_STYLE = {
  passage: {
    fontFamily: '"Times New Roman", Times, serif',
    fontSize: "18px",
  },
  question: {
    fontFamily: '"Times New Roman", Times, serif',
    fontSize: "18px",
  },
  choice: {
    fontFamily: '"Times New Roman", Times, serif',
    fontSize: "18px",
  },
} as const;

// 커스텀 툴바 컴포넌트
interface CustomToolbarProps {
  quillRef: React.RefObject<ReactQuill | null>;
  editorId: string;
}

const CustomToolbar: React.FC<CustomToolbarProps> = ({
  quillRef,
  editorId,
}) => {
  const handleFormat = (format: string, value?: any) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const selection = quill.getSelection();
    if (!selection) return;

    if (format === "align") {
      quill.format("align", value === "left" ? false : value);
    } else {
      const currentValue = quill.getFormat(selection)[format];
      quill.format(format, !currentValue);
    }

    quill.focus();
  };

  return (
    <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
      {/* Text Formatting */}
      <div className="flex items-center gap-1 mr-3">
        <button
          type="button"
          onClick={() => handleFormat("bold")}
          className="p-2 rounded hover:bg-gray-200 text-gray-600 cursor-pointer"
          title="Bold (Ctrl+B)"
        >
          <BoldIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat("italic")}
          className="p-2 rounded hover:bg-gray-200 text-gray-600 cursor-pointer"
          title="Italic (Ctrl+I)"
        >
          <ItalicIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat("underline")}
          className="p-2 rounded hover:bg-gray-200 text-gray-600 cursor-pointer"
          title="Underline (Ctrl+U)"
        >
          <UnderlineIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Alignment */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => handleFormat("align", "left")}
          className="p-2 rounded hover:bg-gray-200 text-gray-600 cursor-pointer"
          title="Align Left"
        >
          <Bars3BottomLeftIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat("align", "center")}
          className="p-2 rounded hover:bg-gray-200 text-gray-600 cursor-pointer"
          title="Align Center"
        >
          <Bars3Icon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => handleFormat("align", "right")}
          className="p-2 rounded hover:bg-gray-200 text-gray-600 cursor-pointer"
          title="Align Right"
        >
          <Bars3BottomRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const ExamPaperEditQuestionsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [editedPassage, setEditedPassage] = useState("");
  const [editedQuestion, setEditedQuestion] = useState("");
  const [currentChoices, setCurrentChoices] = useState<any[]>([]);
  const [correctChoiceId, setCorrectChoiceId] = useState("");
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  // const [initialDataLoaded, setInitialDataLoaded] = useState(false); // 테스트: 제거
  const [isDirty, setIsDirty] = useState(false);
  const [answerType, setAnswerType] = useState<string>("multiple_choice");
  const [shortAnswer, setShortAnswer] = useState("");

  // Quill 에디터 참조
  const passageQuillRef = useRef<ReactQuill>(null);
  const questionQuillRef = useRef<ReactQuill>(null);
  const choiceQuillRefs = useRef<(ReactQuill | null)[]>([]);

  // Quill 모듈 등록 확인 및 설정
  useEffect(() => {
    const setupQuillFormula = () => {
      if (typeof window !== "undefined" && window.Quill) {
        try {
          window.katex = katex;
          const Embed = window.Quill.import("blots/embed");

          class FormulaBlot extends Embed {
            static blotName = "formula";
            static tagName = "span";
            static className = "ql-formula";

            static create(value: string) {
              const node = super.create(value);
              if (typeof value === "string") {
                try {
                  window.katex.render(value, node, {
                    throwOnError: false,
                    displayMode: false,
                  });
                  node.setAttribute("data-value", value);
                } catch (e) {
                  node.textContent = value;
                }
              }
              return node;
            }

            static value(node: Element) {
              return node.getAttribute("data-value");
            }
          }

          try {
            window.Quill.register(FormulaBlot, true);
            console.log("Formula blot registered successfully");
          } catch (e) {
            console.warn(
              "Formula blot already registered or registration failed:",
              e
            );
          }
        } catch (error) {
          console.error("Quill formula setup failed:", error);
        }
      }
    };

    const timer = setTimeout(setupQuillFormula, 100);
    return () => clearTimeout(timer);
  }, []);

  // Query exam paper data
  const {
    data: examPaper,
    isLoading,
    error,
    refetch: refetchExamPaper,
  } = useQueryGetExamPaper(id || "");

  // Query exam config data
  const { data: examConfig } = useQueryGetExamConfig(
    examPaper?.exam_type || "",
    {
      enabled: !!examPaper?.exam_type,
    }
  );

  // 문제 업데이트 뮤테이션
  const updateQuestionMutation = useUpdateQuestion();

  // 현재 섹션의 문제들
  const currentModuleQuestions = React.useMemo(() => {
    if (!examPaper || !examPaper.sections) return [];

    const section = examPaper.sections[currentModuleIndex];
    return section?.questions || [];
  }, [examPaper, currentModuleIndex]);

  // 현재 선택된 문제
  const currentQuestion = currentModuleQuestions[currentQuestionIndex];



  // 문제 변경될 때마다 에디터 상태 업데이트
  useEffect(() => {
    if (currentQuestion) {
      setEditedPassage(currentQuestion.passage || "");
      setEditedQuestion(currentQuestion.question || "");
      setCurrentChoices(currentQuestion.choices || []);
      setCorrectChoiceId(currentQuestion.correct_choice_id || "");
      setIsDirty(false);
      setAnswerType(currentQuestion.answer_type || "multiple_choice");
      setShortAnswer(
        currentQuestion.answer_type === "short_answer"
          ? (currentQuestion as any).correct_text_answer || ""
          : ""
      );

      // Shared passage 관련 상태 설정
      setLinkedPassageId(currentQuestion.passage_id || null);
    }
  }, [currentQuestion?.id]);

  // 데이터 변경 감지 로직
  useEffect(() => {
    if (!currentQuestion) return;
    const isChanged =
      editedPassage !== (currentQuestion.passage || "") ||
      editedQuestion !== (currentQuestion.question || "") ||
      answerType !== (currentQuestion.answer_type || "multiple_choice") ||
      (answerType === "multiple_choice"
        ? correctChoiceId !== currentQuestion.correct_choice_id
        : shortAnswer !== ((currentQuestion as any).correct_text_answer || ""));
    setIsDirty(isChanged);
  }, [
    editedPassage,
    editedQuestion,
    correctChoiceId,
    answerType,
    shortAnswer,
    currentQuestion?.id,
  ]);

  // 뒤로 가기
  const handleBack = () => {
    if (isDirty) {
      if (
        window.confirm("저장되지 않은 변경사항이 있습니다. 저장하시겠습니까?")
      ) {
        handleSave(true).then(() => {
          navigate(`/exam_papers/${id}`);
        });
      } else {
        navigate(`/exam_papers/${id}`);
      }
    } else {
      navigate(`/exam_papers/${id}`);
    }
  };

  // 모듈 변경
  const handleModuleChange = (newModuleIndex: number) => {
    if (isDirty) {
      handleSave().then(() => {
        setCurrentModuleIndex(newModuleIndex);
        setCurrentQuestionIndex(0);
      });
    } else {
      setCurrentModuleIndex(newModuleIndex);
      setCurrentQuestionIndex(0);
    }
  };

  // 문제 번호 변경
  const handleQuestionChange = (questionNumber: number) => {
    if (isDirty) {
      handleSave().then(() => {
        setCurrentQuestionIndex(questionNumber - 1);
      });
    } else {
      setCurrentQuestionIndex(questionNumber - 1);
    }
  };

    // 저장 처리
  const handleSave = async (showMessage = false) => {
    if (!currentQuestion) return Promise.resolve();
    if (!isDirty) return Promise.resolve();
    
    // 저장 시점의 문제 정보를 캡처하여 race condition 방지 (깊은 복사 사용)
    const questionToSave = currentQuestion;
    const passageToSave = editedPassage;
    const questionContentToSave = editedQuestion;
    const answerTypeToSave = answerType;
    const choicesToSave = currentChoices.map(choice => ({ ...choice })); // 깊은 복사
    const correctChoiceToSave = correctChoiceId;
    const shortAnswerToSave = shortAnswer;
    
    setSaving(true);
    try {
      // Question 저장
      await updateQuestionMutation.mutateAsync({
        id: questionToSave.id,
        data: {
          passage: passageToSave,
          question: questionContentToSave,
          answer_type: answerTypeToSave,
          ...(answerTypeToSave === "multiple_choice"
            ? {
                choices: choicesToSave,
                correct_choice_id: correctChoiceToSave,
                correct_text_answer: undefined,
              }
            : {
                choices: undefined,
                correct_choice_id: undefined,
                correct_text_answer: shortAnswerToSave,
              }),
        },
      });

      // Shared passage 저장 (변경된 경우에만)
      if (
        linkedPassageId &&
        linkedPassage &&
        editedSharedPassageContent !== linkedPassage.content
      ) {
        await updateSharedPassageMutation.mutateAsync({
          id: linkedPassageId,
          data: { content: editedSharedPassageContent },
        });
      }

      setLastSaved(new Date());
      setIsDirty(false);
      if (showMessage) {
        message.success("Question saved successfully");
      }
      refetchExamPaper();
      return Promise.resolve();
    } catch (error) {
      if (showMessage) {
        message.error("Failed to save question");
      }
      return Promise.reject(error);
    } finally {
      setSaving(false);
    }
  };



  // 선택지 편집기 (텍스트 변경 핸들러)
  const handleChoiceTextChange = (index: number, value: string) => {
    const newChoices = [...currentChoices];
    newChoices[index].text = value;
    setCurrentChoices(newChoices);
    setIsDirty(true);
  };

  // 마지막 저장 시간 표시
  const getLastSavedText = () => {
    if (!lastSaved) return "";
    return `Last saved: ${lastSaved.toLocaleTimeString()}`;
  };

  // 이미지 차단 로직 - 간단하고 효과적인 솔루션
  useEffect(() => {
    const setupImageBlocking = () => {
      const quillRefs = [
        { ref: passageQuillRef, name: "passage" },
        { ref: questionQuillRef, name: "question" },
        ...choiceQuillRefs.current.map((ref, index) => ({
          ref: { current: ref },
          name: `choice-${index}`,
        })),
      ];

      const cleanupFunctions: (() => void)[] = [];

      quillRefs.forEach(({ ref, name }) => {
        if (ref.current) {
          const quill = ref.current.getEditor();

          const pasteHandler = async (e: ClipboardEvent) => {
            const items = e.clipboardData?.items;
            if (items) {
              for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf("image") !== -1) {
                  console.log(
                    `Image paste detected in ${name} editor - uploading...`
                  );
                  e.preventDefault();
                  e.stopPropagation();
                  e.stopImmediatePropagation();

                  // 이미지 파일 얻기
                  const file = items[i].getAsFile();
                  if (file) {
                    try {
                      const response = await apiUploadImage(file);
                      const range = quill.getSelection();
                      if (range) {
                        quill.insertEmbed(range.index, "image", response.url);
                        quill.setSelection(range.index + 1);
                      }
                      console.log(
                        `Image uploaded and inserted in ${name} editor`
                      );
                    } catch (error) {
                      console.error("Image upload failed:", error);
                      message.error("Failed to upload image");
                    }
                  }
                  return false;
                }
              }
            }
          };

          quill.root.addEventListener("paste", pasteHandler, true);
          cleanupFunctions.push(() => {
            quill.root.removeEventListener("paste", pasteHandler, true);
          });
        }
      });

      return () => {
        cleanupFunctions.forEach((cleanup) => cleanup());
      };
    };

    // 약간의 지연을 두고 설정
    const timer = setTimeout(setupImageBlocking, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [currentQuestion]); // currentQuestion이 변경될 때마다 재설정

  // 이미지 붙여넣기 핸들러 - 더 이상 사용되지 않음 (차단됨)
  // const handlePasteImage = async (quill: any, event: ClipboardEvent) => {
  //   // 이미지는 이제 paste 이벤트에서 차단되므로 이 함수는 호출되지 않음
  // };

  // 현재 subject의 passage_type을 확인하는 함수
  const getCurrentSubjectPassageType = () => {
    if (!examConfig || !examPaper) return "individual"; // 기본값

    const currentSubject = examConfig.subjects.find(
      (subject) => subject.subject === examPaper.subject
    );

    return currentSubject?.passage_type || "individual"; // 기본값
  };

  // passage가 필요한지 확인
  const isPassageRequired = getCurrentSubjectPassageType() !== "none";
  const isSharedPassage = getCurrentSubjectPassageType() === "shared";

  // Shared passage 관련 상태
  const [linkedPassageId, setLinkedPassageId] = useState<string | null>(null);
  const [isCreatingNewPassage, setIsCreatingNewPassage] = useState(false);
  const [newPassageContent, setNewPassageContent] = useState("");
  const [editedSharedPassageContent, setEditedSharedPassageContent] =
    useState("");

  // 연결된 shared passage 조회
  const { data: linkedPassage } = useQueryGetPassage(linkedPassageId || "", {
    enabled: !!linkedPassageId,
  });

  // linkedPassage가 로드되면 editedSharedPassageContent 초기화
  useEffect(() => {
    if (linkedPassage) {
      setEditedSharedPassageContent(linkedPassage.content || "");
    }
  }, [linkedPassage]);

  // Shared passage 변경 감지
  useEffect(() => {
    if (!linkedPassage) return;
    const isSharedPassageChanged =
      editedSharedPassageContent !== (linkedPassage.content || "");
    if (isSharedPassageChanged) {
      setIsDirty(true);
    }
  }, [editedSharedPassageContent, linkedPassage?.id]);

  // Shared passage 수정 뮤테이션
  const updateSharedPassageMutation = useUpdatePassage();

  // Shared passage 생성 뮤테이션
  const createPassageMutation = useCreatePassage();

  // 새 passage 생성 시작
  const handleStartCreatingNewPassage = () => {
    setIsCreatingNewPassage(true);
    setNewPassageContent("");
  };

  // 새 passage 저장
  const handleSaveNewPassage = async () => {
    if (!newPassageContent.trim()) {
      return;
    }

    try {
      const result = await createPassageMutation.mutateAsync({
        exam_paper_id: examPaper?.id || "",
        content: newPassageContent,
      });

      if (result?.id) {
        setLinkedPassageId(result.id);
        setIsCreatingNewPassage(false);
        setNewPassageContent("");

        // Question을 업데이트하여 passage_id 연결
        if (currentQuestion) {
          await updateQuestionMutation.mutateAsync({
            id: currentQuestion.id,
            data: {
              passage_id: result.id,
              passage: "", // individual passage는 비움
            },
          });
          refetchExamPaper();
        }
      }
    } catch (error) {
      console.error("Failed to create passage:", error);
    }
  };

  // 새 passage 생성 취소
  const handleCancelCreatingNewPassage = () => {
    setIsCreatingNewPassage(false);
    setNewPassageContent("");
  };

  // 이전 문제의 passage에 링크
  const handleLinkToPreviousPassage = async () => {
    const prevQuestion = currentModuleQuestions[currentQuestionIndex - 1];
    if (prevQuestion?.passage_id && currentQuestion) {
      setLinkedPassageId(prevQuestion.passage_id);
      await updateQuestionMutation.mutateAsync({
        id: currentQuestion.id,
        data: {
          passage_id: prevQuestion.passage_id,
          passage: "", // individual passage는 비움
        },
      });
      refetchExamPaper();
    }
  };

  // 다음 문제의 passage에 링크
  const handleLinkToNextPassage = async () => {
    const nextQuestion = currentModuleQuestions[currentQuestionIndex + 1];
    if (nextQuestion?.passage_id && currentQuestion) {
      setLinkedPassageId(nextQuestion.passage_id);
      await updateQuestionMutation.mutateAsync({
        id: currentQuestion.id,
        data: {
          passage_id: nextQuestion.passage_id,
          passage: "", // individual passage는 비움
        },
      });
      refetchExamPaper();
    }
  };

  // Shared passage 연결 해제
  const handleUnlinkPassage = async () => {
    setLinkedPassageId(null);
    if (currentQuestion) {
      await updateQuestionMutation.mutateAsync({
        id: currentQuestion.id,
        data: {
          passage_id: null,
          passage: "", // individual passage로 되돌림
        },
      });
      refetchExamPaper();
    }
  };

  // Shared passage 수정 핸들러
  const handleSharedPassageChange = (content: string) => {
    setEditedSharedPassageContent(content);
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <PageContainer>
        <div className="h-screen flex items-center justify-center">
          <Spin size="large">
            <div className="pt-4">Loading...</div>
          </Spin>
        </div>
      </PageContainer>
    );
  }

  // 에러 상태
  if (error || !examPaper) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-red-600">
          <Text>Failed to load exam paper information. Please try again.</Text>
          <Button onClick={handleBack} className="mt-4">
            Back to Detail
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer wide>
      <div className="max-w-screen-2xl mx-auto px-4">
        <div className="p-6">
          {/* 헤더 */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <Button onClick={handleBack} plain className="rounded-full p-2">
                <ArrowLeftIcon className="h-5 w-5" />
              </Button>
              <Heading level={3}>Edit Questions: {examPaper.title}</Heading>
            </div>
            <div className="flex items-center gap-4">
              {/* 수동 저장 버튼 */}
              <Button
                onClick={() => handleSave(true)}
                disabled={saving || !isDirty}
                className="min-w-20"
              >
                {saving ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Save
                  </div>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </div>

          {/* 모듈 선택 탭 */}
          <div className="mb-6">
            <Tabs
              activeKey={currentModuleIndex.toString()}
              onChange={(key) => handleModuleChange(parseInt(key))}
              type="card"
              className="mb-4"
              items={examPaper.sections?.map((section: any, index: number) => ({
                key: index.toString(),
                label: `Section ${index + 1}`,
              }))}
            />

            {/* 현재 섹션 정보 */}
            <div className="bg-blue-50 p-3 rounded-md mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <Text className="font-medium">
                    Section {currentModuleIndex + 1}
                  </Text>
                  <div className="text-sm text-gray-600 mt-1">
                    <span>
                      {examPaper.sections?.[currentModuleIndex]?.duration || 0}{" "}
                      minutes
                    </span>
                    <span className="mx-2">•</span>
                    <span>{currentModuleQuestions.length} questions</span>
                  </div>
                </div>
                <Button
                  color="white"
                  onClick={async () => {
                    if (isDirty) {
                      await handleSave();
                    }
                    navigate(
                      `/exam_papers/${id}/preview/${currentModuleIndex + 1}`
                    );
                  }}
                >
                  Preview All
                </Button>
              </div>
            </div>

            {/* 문제 번호 선택 */}
            <QuestionNumberSelector
              questions={currentModuleQuestions}
              currentQuestionIndex={currentQuestionIndex}
              onQuestionChange={handleQuestionChange}
            />
          </div>

          {/* 메인 에디터 */}
          {currentQuestion ? (
            <>
              <div className="flex justify-end mb-4">
                <Button
                  color="white"
                  onClick={async () => {
                    if (isDirty) {
                      await handleSave();
                    }
                    navigate(
                      `/exam_papers/${id}/preview/${
                        currentModuleIndex + 1
                      }?question=${currentQuestionIndex + 1}`
                    );
                  }}
                >
                  Preview
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-full overflow-hidden">
                {/* 좌측: 지문 영역 */}
                <div className="bg-gray-50 p-4 rounded-lg h-full flex flex-col">
                  <div className="mb-3 flex justify-between items-center">
                    <Text className="font-medium">Passage</Text>
                  </div>
                  <div className="quill-container w-full max-w-full flex flex-col overflow-auto min-h-0 flex-1 border border-gray-300 bg-white rounded">
                    {isPassageRequired ? (
                      isSharedPassage ? (
                        // Shared passage UI
                        linkedPassageId && linkedPassage ? (
                          <>
                            <div className="flex items-center justify-between p-2 bg-blue-50 border-b">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-blue-700">
                                  Shared Passage
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  color="red"
                                  onClick={handleUnlinkPassage}
                                >
                                  Unlink
                                </Button>
                              </div>
                            </div>
                            <ReactQuill
                              theme="snow"
                              value={editedSharedPassageContent}
                              onChange={handleSharedPassageChange}
                              modules={noToolbarModules}
                              style={EDITOR_STYLE.passage}
                              className="font-times max-w-full flex-1"
                              ref={passageQuillRef}
                            />
                          </>
                        ) : isCreatingNewPassage ? (
                          // Creating new passage UI
                          <>
                            <div className="flex items-center justify-between p-2 bg-green-50 border-b">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-green-700">
                                  Creating New Shared Passage
                                </span>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  plain
                                  onClick={handleCancelCreatingNewPassage}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={handleSaveNewPassage}
                                  disabled={
                                    !newPassageContent.trim() ||
                                    createPassageMutation.isPending
                                  }
                                >
                                  {createPassageMutation.isPending
                                    ? "Saving..."
                                    : "Save"}
                                </Button>
                              </div>
                            </div>
                            <CustomToolbar
                              quillRef={passageQuillRef}
                              editorId="passage"
                            />
                            <ReactQuill
                              theme="snow"
                              value={newPassageContent}
                              onChange={setNewPassageContent}
                              modules={noToolbarModules}
                              style={EDITOR_STYLE.passage}
                              className="font-times max-w-full flex-1"
                              ref={passageQuillRef}
                              placeholder="Enter passage content here..."
                            />
                          </>
                        ) : (
                          // No shared passage linked
                          <div className="flex-1 flex flex-col items-center justify-center p-8">
                            <Text className="text-gray-500 text-center mb-4">
                              No shared passage selected
                            </Text>
                            <div className="grid grid-cols-1 gap-2 md:flex md:gap-3">
                              {currentQuestionIndex > 0 &&
                                currentModuleQuestions[currentQuestionIndex - 1]
                                  ?.passage_id && (
                                  <Button
                                    color="white"
                                    onClick={handleLinkToPreviousPassage}
                                    className="w-full md:w-auto"
                                  >
                                    Link to Previous Passage
                                  </Button>
                                )}
                              <Button
                                onClick={handleStartCreatingNewPassage}
                                className="w-full md:w-auto"
                              >
                                Create New Passage
                              </Button>
                              {currentQuestionIndex <
                                currentModuleQuestions.length - 1 &&
                                currentModuleQuestions[currentQuestionIndex + 1]
                                  ?.passage_id && (
                                  <Button
                                    color="white"
                                    onClick={handleLinkToNextPassage}
                                    className="w-full md:w-auto"
                                  >
                                    Link to Next Passage
                                  </Button>
                                )}
                            </div>
                          </div>
                        )
                      ) : (
                        // Individual passage UI (기존 코드)
                        <>
                          <CustomToolbar
                            quillRef={passageQuillRef}
                            editorId="passage"
                          />
                          <ReactQuill
                            theme="snow"
                            value={editedPassage}
                            onChange={setEditedPassage}
                            modules={noToolbarModules}
                            style={EDITOR_STYLE.passage}
                            className="font-times max-w-full flex-1"
                            ref={passageQuillRef}
                          />
                        </>
                      )
                    ) : (
                      <div className="flex-1 flex items-center justify-center">
                        <Text className="text-gray-500 text-center">
                          No passage input required for {examPaper.exam_type}{" "}
                          {getPaperTypeLabel(examPaper.subject)}
                        </Text>
                      </div>
                    )}
                  </div>
                </div>

                {/* 우측: 문제 및 선택지/단답형 영역 */}
                <div className="bg-gray-50 p-4 rounded-lg overflow-hidden">
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <Text className="font-medium">
                        Question {currentQuestionIndex + 1}
                      </Text>
                    </div>

                    <div className="quill-container w-full max-w-full flex flex-col overflow-auto min-h-0 flex-1 border border-gray-300 bg-white rounded">
                      <CustomToolbar
                        quillRef={questionQuillRef}
                        editorId="question"
                      />
                      <ReactQuill
                        theme="snow"
                        value={editedQuestion}
                        onChange={setEditedQuestion}
                        modules={noToolbarModules}
                        style={EDITOR_STYLE.question}
                        className="font-times max-w-full"
                        ref={questionQuillRef}
                      />
                    </div>
                  </div>

                  {/* 정답 타입 선택 */}
                  <div className="mb-4">
                    <Text className="mb-2 font-medium">Answer Type:</Text>
                    <Select
                      value={answerType}
                      onChange={(e) => setAnswerType(e.target.value)}
                      className="w-52"
                    >
                      <option value="multiple_choice">Multiple Choice</option>
                      <option value="short_answer">Short Answer</option>
                    </Select>
                  </div>

                  {/* 객관식/단답형 분기 */}
                  {answerType === "multiple_choice" ? (
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <Text className="font-medium">Choices</Text>
                      </div>
                      {currentChoices.map((choice, index) => (
                        <div
                          key={choice.id}
                          className="flex items-start mb-3 gap-3"
                        >
                          <div className="mt-2">
                            <label
                              htmlFor={`choice_${choice.id}`}
                              className="inline-flex items-center cursor-pointer"
                            >
                              <input
                                type="radio"
                                id={`choice_${choice.id}`}
                                name="correctChoice"
                                checked={correctChoiceId === choice.id}
                                onChange={() => setCorrectChoiceId(choice.id)}
                                className="sr-only"
                              />
                              <span
                                className={`w-6 h-6 border border-gray-300 rounded-full flex items-center justify-center ${
                                  correctChoiceId === choice.id
                                    ? "bg-green-500 border-green-500"
                                    : "bg-white"
                                }`}
                              >
                                {correctChoiceId === choice.id && (
                                  <span className="w-3 h-3 bg-white rounded-full"></span>
                                )}
                              </span>
                            </label>
                          </div>
                          <div className="flex-1 w-full max-w-full flex flex-col overflow-auto min-h-0 relative border border-gray-300 bg-white">
                            <div
                              className={`quill-container w-full max-w-full overflow-hidden relative ${
                                correctChoiceId === choice.id
                                  ? "border-2 border-green-300 rounded box-border"
                                  : ""
                              }`}
                            >
                              {correctChoiceId === choice.id && (
                                <div className="absolute top-0 left-0 bg-green-100 text-green-700 px-1 py-0 text-[9px] font-bold rounded-br z-10 border border-green-300">
                                  Correct
                                </div>
                              )}
                              <ReactQuill
                                theme="snow"
                                value={choice.text}
                                onChange={(value) =>
                                  handleChoiceTextChange(index, value)
                                }
                                modules={noToolbarModules}
                                style={EDITOR_STYLE.choice}
                                className="font-times max-w-full"
                                ref={(el) => {
                                  choiceQuillRefs.current[index] = el;
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mb-4">
                      <Text className="font-medium mb-2">Short Answer</Text>
                      <Input.TextArea
                        value={shortAnswer}
                        onChange={(e) => setShortAnswer(e.target.value)}
                        rows={3}
                        placeholder="Enter the correct answer here..."
                      />
                      <div className="mt-6 text-xl font-bold">
                        Answer Preview:{" "}
                        <span
                          dangerouslySetInnerHTML={{
                            __html: convertToFraction(shortAnswer),
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* 문제 카테고리 선택 */}
                  <div className="mb-4">
                    <Text className="mb-2 font-medium">Category:</Text>
                    <CategorySelector
                      question={currentQuestion}
                      examType={examPaper?.exam_type}
                      subject={examPaper?.subject}
                    />
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-gray-50 p-8 rounded-lg text-center">
              <Text className="text-gray-500">
                No questions available in this module
              </Text>
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

export default ExamPaperEditQuestionsPage;
